import { jsPDF } from "jspdf";

const C = {
  primary: [72, 52, 212],
  primaryDark: [30, 20, 100],
  gold: [178, 138, 30],
  goldLight: [232, 199, 92],
  goldPale: [252, 246, 219],
  text: [40, 42, 60],
  textLight: [110, 118, 135],
  white: [255, 255, 255],
  paper: [253, 251, 244],
  paperShade: [246, 242, 226],
};

const PAGE_W = 297;
const PAGE_H = 210;

const loadImageAsBase64 = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve({
        data: canvas.toDataURL("image/png"),
        w: img.naturalWidth,
        h: img.naturalHeight,
      });
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const drawBackground = (doc) => {
  doc.setFillColor(...C.paper);
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");

  doc.setFillColor(...C.paperShade);
  doc.circle(-20, -20, 90, "F");
  doc.setFillColor(...C.paperShade);
  doc.circle(PAGE_W + 20, PAGE_H + 20, 90, "F");

  doc.setFillColor(...C.paper);
  doc.rect(20, 20, PAGE_W - 40, PAGE_H - 40, "F");
};

const drawBorder = (doc) => {
  const outer = 7;
  const inner = 11;

  doc.setDrawColor(...C.gold);
  doc.setLineWidth(1.4);
  doc.rect(outer, outer, PAGE_W - outer * 2, PAGE_H - outer * 2);

  doc.setDrawColor(...C.goldLight);
  doc.setLineWidth(0.4);
  doc.rect(inner, inner, PAGE_W - inner * 2, PAGE_H - inner * 2);

  const drawCorner = (x, y, flipX, flipY) => {
    const size = 14;
    const sx = flipX ? -1 : 1;
    const sy = flipY ? -1 : 1;
    doc.setDrawColor(...C.gold);
    doc.setLineWidth(0.8);
    doc.line(x, y, x + size * sx, y);
    doc.line(x, y, x, y + size * sy);
    doc.setLineWidth(0.4);
    doc.line(x + 3 * sx, y + 3 * sy, x + size * sx, y + 3 * sy);
    doc.line(x + 3 * sx, y + 3 * sy, x + 3 * sx, y + size * sy);

    doc.setFillColor(...C.gold);
    doc.circle(x, y, 1.8, "F");
    doc.setFillColor(...C.paper);
    doc.circle(x, y, 0.9, "F");
  };

  drawCorner(outer, outer, false, false);
  drawCorner(PAGE_W - outer, outer, true, false);
  drawCorner(outer, PAGE_H - outer, false, true);
  drawCorner(PAGE_W - outer, PAGE_H - outer, true, true);
};

const drawSeal = (doc, cx, cy) => {
  doc.setFillColor(...C.gold);
  doc.circle(cx, cy, 12, "F");
  doc.setFillColor(...C.goldLight);
  doc.circle(cx, cy, 10, "F");
  doc.setFillColor(...C.gold);
  doc.circle(cx, cy, 8.5, "F");

  doc.setTextColor(...C.white);
  doc.setFont("times", "bold");
  doc.setFontSize(9);
  doc.text("UT", cx, cy + 1.2, { align: "center" });
  doc.setFontSize(5);
  doc.text("★ TALENTO ★", cx, cy + 5, { align: "center" });

  doc.setDrawColor(...C.gold);
  doc.setLineWidth(0.4);
  for (let a = 0; a < 12; a++) {
    const rad = (a * Math.PI * 2) / 12;
    const x1 = cx + Math.cos(rad) * 12.5;
    const y1 = cy + Math.sin(rad) * 12.5;
    const x2 = cx + Math.cos(rad) * 14;
    const y2 = cy + Math.sin(rad) * 14;
    doc.line(x1, y1, x2, y2);
  }
};

const drawHeader = (doc, logo) => {
  if (logo) {
    const targetH = 20;
    const targetW = (logo.w / logo.h) * targetH;
    const x = PAGE_W / 2 - targetW / 2;
    doc.addImage(logo.data, "PNG", x, 20, targetW, targetH);
  }

  doc.setDrawColor(...C.gold);
  doc.setLineWidth(0.5);
  doc.line(PAGE_W / 2 - 30, 45, PAGE_W / 2 - 5, 45);
  doc.line(PAGE_W / 2 + 5, 45, PAGE_W / 2 + 30, 45);

  doc.setFillColor(...C.gold);
  doc.circle(PAGE_W / 2, 45, 1, "F");
};

const drawTitle = (doc) => {
  doc.setTextColor(...C.primaryDark);
  doc.setFont("times", "bold");
  doc.setFontSize(34);
  doc.text("CERTIFICADO", PAGE_W / 2, 60, { align: "center" });

  doc.setTextColor(...C.gold);
  doc.setFont("times", "italic");
  doc.setFontSize(12);
  doc.text("de Reconocimiento", PAGE_W / 2, 68, { align: "center" });

  doc.setTextColor(...C.textLight);
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.text("Se otorga el presente certificado a:", PAGE_W / 2, 78, {
    align: "center",
  });
};

const drawName = (doc, name) => {
  doc.setTextColor(...C.primary);
  doc.setFont("times", "bolditalic");
  doc.setFontSize(28);
  const displayName = (name || "Estudiante").toUpperCase();
  doc.text(displayName, PAGE_W / 2, 93, { align: "center" });

  const nameW = doc.getTextWidth(displayName);
  const lineY = 97;
  doc.setDrawColor(...C.gold);
  doc.setLineWidth(0.7);
  const lineWidth = Math.min(nameW / 2 + 12, 110);
  doc.line(
    PAGE_W / 2 - lineWidth,
    lineY,
    PAGE_W / 2 + lineWidth,
    lineY,
  );

  doc.setFillColor(...C.gold);
  doc.circle(PAGE_W / 2 - lineWidth, lineY, 0.8, "F");
  doc.circle(PAGE_W / 2 + lineWidth, lineY, 0.8, "F");
};

const drawDescription = (doc) => {
  doc.setTextColor(...C.text);
  doc.setFont("times", "normal");
  doc.setFontSize(11);
  doc.text(
    "Por haber completado con éxito el proceso de identificación de talentos,",
    PAGE_W / 2,
    107,
    { align: "center" },
  );
  doc.text("descubriendo sus fortalezas dominantes:", PAGE_W / 2, 112, {
    align: "center",
  });
};

const drawTalentos = (doc, talentos) => {
  if (!talentos?.length) return;

  const startY = 122;
  const list = talentos.slice(0, 10);
  const cols = list.length > 4 ? 2 : 1;
  const rowH = 7.5;
  const colW = 105;

  list.forEach((t, i) => {
    const col = cols === 2 ? i % 2 : 0;
    const row = cols === 2 ? Math.floor(i / 2) : i;
    const totalW = cols * colW;
    const startX = PAGE_W / 2 - totalW / 2;
    const x = startX + col * colW + 6;
    const y = startY + row * rowH;

    doc.setFillColor(...C.goldPale);
    doc.roundedRect(x - 4, y - 4, colW - 8, rowH - 1, 2, 2, "F");

    doc.setFillColor(...C.gold);
    doc.circle(x, y - 0.8, 2.6, "F");
    doc.setTextColor(...C.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(String(t.ranking ?? i + 1), x, y + 0.6, { align: "center" });

    doc.setTextColor(...C.primaryDark);
    doc.setFont("times", "bold");
    doc.setFontSize(11);
    const name = (t.nombre || t.talento_nombre || "").trim();
    doc.text(name, x + 6, y + 0.6);
  });
};

const drawFooter = (doc) => {
  const fecha = new Date().toLocaleDateString("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  drawSeal(doc, PAGE_W / 2, PAGE_H - 30);

  doc.setTextColor(...C.primaryDark);
  doc.setFont("times", "bold");
  doc.setFontSize(12);
  doc.text(
    "Certificado por Universidad del Talento",
    PAGE_W / 2,
    PAGE_H - 14,
    { align: "center" },
  );

  doc.setTextColor(...C.textLight);
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.text(`Otorgado el ${fecha}`, PAGE_W / 2, PAGE_H - 9, {
    align: "center",
  });
};

export const generateDiplomaPDF = async ({ nombre, talentos }) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const logo = await loadImageAsBase64("/assets/images/logo.png");

  drawBackground(doc);
  drawBorder(doc);
  drawHeader(doc, logo);
  drawTitle(doc);
  drawName(doc, nombre);
  drawDescription(doc);
  drawTalentos(doc, talentos);
  drawFooter(doc);

  return doc;
};

export const downloadDiplomaPDF = async ({ nombre, talentos }) => {
  const doc = await generateDiplomaPDF({ nombre, talentos });
  if (!doc) return;
  const safeName = (nombre || "Certificado")
    .replace(/[^a-zA-Z0-9-_ ]/g, "")
    .trim()
    .replace(/\s+/g, "-");
  doc.save(`Certificado-${safeName}.pdf`);
};

export const previewDiplomaPDF = async ({ nombre, talentos }) => {
  const doc = await generateDiplomaPDF({ nombre, talentos });
  if (!doc) return null;
  return doc.output("bloburl");
};
